import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { shareTypeMapping, NONE, PLAYLIST_STATUS } from 'shared/const';

import DragAndDrop from 'shared/molecules/DragAndDrop';
import ItemBox from 'shared/molecules/ItemBox';

import {
  getPlaylistDetailUrl,
  formatDate,
  newSelectTranslation,
} from 'shared/utils';
import { usePlaylist } from '../../../../context';

const DragAndDropWrapper = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

const PlaylistTitleBoxList = ({ selectedRef }) => {
  const { t } = useTranslation();
  const {
    canManagePlaylists,
    canSortPlaylists,
    isGlobalOrganization,
    locale,
    previewBaseUrl,
    seeActivityHandler,
    hasSubscription: canEditPlaylists,
    dialogs: {
      addContentHandler,
      deleteHandler,
      editPlaylistHandler,
      pauseHandler,
      publishHandler,
      setSelectedPlaylistForReminder,
      openSharePlaylistHandler,
      openTransferOwnershipHandler,
    },
    playlists: {
      listOwnedPlaylists,
      playlistOrderHandler,
      selectedPlaylistId,
      selectPlaylistHandler,
    },
  } = usePlaylist();

  const [playlists, setPlaylists] = useState([]);

  const getMenuItems = (
    playlistId,
    status,
    disableActivity,
    notShared,
    lastReminderAt
  ) => {
    const result = [
      {
        id: 'editSettings',
        label: t('playlist.editDetails'),
        handler: editPlaylistHandler,
        disabled: !canEditPlaylists,
      },
      {
        id: 'editContent',
        label: t('playlist.editContent'),
        handler: () => {
          selectPlaylistHandler(playlistId, true);
          addContentHandler();
        },
        disabled: !canEditPlaylists,
      },
    ];

    if (!notShared && status === PLAYLIST_STATUS.PUBLISHED) {
      result.push({
        id: 'sendReminder',
        label: t('playlist.sendReminder'),
        handler: () => setSelectedPlaylistForReminder(playlistId),
        disabled: new Date() - new Date(lastReminderAt) < 48 * 60 * 60 * 1000,
      });
    }

    if (!isGlobalOrganization) {
      result.push(
        ...[
          {
            id: 'sharePlaylist',
            label: t('playlist.share'),
            handler: openSharePlaylistHandler,
            disabled: !canEditPlaylists || status !== PLAYLIST_STATUS.PUBLISHED,
          },
          {
            id: 'viewActivity',
            label: t('playlist.seeActivity'),
            handler: seeActivityHandler,
            disabled: disableActivity,
          },
          {
            id: 'transferPlaylist',
            label: t('playlist.transferOwnership'),
            handler: openTransferOwnershipHandler,
            disabled: !canEditPlaylists,
          },
          {
            id: 'deletePlaylist',
            label: t('playlist.removePlaylist'),
            handler: (id) => deleteHandler(id),
          },
        ]
      );
    } else if (isGlobalOrganization && status === PLAYLIST_STATUS.DRAFT) {
      result.push({
        id: 'deletePlaylist',
        label: t('playlist.removePlaylist'),
        handler: (id) => deleteHandler(id),
      });
    }

    return result;
  };

  useEffect(() => {
    setPlaylists(
      canSortPlaylists
        ? listOwnedPlaylists
        : [...listOwnedPlaylists].sort((a, b) =>
            b.publishedAt?.localeCompare(a.publishedAt)
          )
    );
  }, [listOwnedPlaylists]);

  const handleDragEnd = (array, id, index) => {
    setPlaylists(array);
    if (playlistOrderHandler) {
      // TODO: Handle errors in sorting properly
      playlistOrderHandler(index + 1, id, array);
    }
  };

  // TODO: Set comparison criteria for when there's no publishing date
  const renderItem = (playlist, idx, playlists) => {
    const notShared =
      playlist?.shareType && shareTypeMapping[playlist.shareType] === NONE;
    const tags = [];

    if (notShared && !isGlobalOrganization) {
      tags.push(t('playlist.notShared'));
    }

    const disableActivity =
      shareTypeMapping[playlist.shareType] === NONE ||
      playlist.status !== PLAYLIST_STATUS.PUBLISHED ||
      (playlist.courses || []).length === 0;

    const buttons = [];

    if (canManagePlaylists) {
      if (
        playlist.status === PLAYLIST_STATUS.PUBLISHED ||
        playlist.status === PLAYLIST_STATUS.TEMPLATE
      ) {
        buttons.push({
          label: t('playlist.pause'),
          handler: () => pauseHandler(playlist.id),
          textStyle: true,
        });
      } else {
        buttons.push({
          label: t('playlist.publish'),
          handler: () => publishHandler(playlist.id),
          disabled: (playlist.courses || []).length === 0,
        });
      }
      buttons.push({
        label: t('playlist.preview'),
        handler: () => {},
        href: `${previewBaseUrl || ''}${getPlaylistDetailUrl(
          playlist,
          locale
        )}`,
        textStyle: true,
        disabled: (playlist.courses || []).length === 0,
      });
    }

    const title = newSelectTranslation(playlist.name, locale);
    return (
      <div ref={idx === playlists.length - 1 ? selectedRef : null}>
        <ItemBox
          animated
          buttons={buttons}
          draggable={canSortPlaylists}
          id={playlist.id}
          menuItems={
            canManagePlaylists
              ? getMenuItems(
                  playlist.id,
                  playlist.status,
                  disableActivity,
                  notShared,
                  playlist.lastReminderAt
                )
              : []
          }
          onClickHandler={selectPlaylistHandler}
          selected={selectedPlaylistId === playlist.id}
          tags={tags}
          theme="light"
          title={title}
          topText={
            playlist.status === PLAYLIST_STATUS.DRAFT
              ? t('playlist.notPublished')
              : t('playlist.published', {
                  publishedAt: playlist.publishedAt
                    ? formatDate({
                        date: playlist.publishedAt,
                      })
                    : '?',
                })
          }
        />
      </div>
    );
  };
  return (
    <DragAndDropWrapper>
      <DragAndDrop
        isLocked={!canSortPlaylists}
        items={playlists}
        onDragEnd={handleDragEnd}
        renderItem={renderItem}
      />
    </DragAndDropWrapper>
  );
};

PlaylistTitleBoxList.propTypes = {
  selectedRef: PropTypes.object,
};

export default PlaylistTitleBoxList;
